// The upload-time warning.
//
// Terms of Service §11 lists what may not be uploaded, but nobody reads a legal
// page before dropping a file. So the same rule is repeated at the two places a
// file actually enters the platform — the media library and Compose — where it
// can still change what someone does.
//
// It is a <details>, not a dismissible banner: a warning that can be dismissed
// forever is a warning that was shown once, and we need it standing at every
// upload. Collapsed it costs one line; expanded it names the categories, which
// is what makes "you were told" true rather than merely arguable.

import { Link } from "react-router-dom";

/** Short enough to scan, specific enough to be a warning rather than a mood. */
const CATEGORIES: Array<[string, string]> = [
  ["Violence and graphic material", "gore, cruelty, atrocity footage, threats"],
  ["Sexual content", "pornography, nudity, and any intimate image shared without consent"],
  ["Child sexual abuse material", "reported to the authorities, permanent ban, no exceptions"],
  ["Hate speech", "attacks on religion, caste, race, sex, gender, orientation, disability"],
  ["Terrorism and extremism", "praise, support, recruitment or instruction"],
  ["Weapons and explosives", "sale or brokering of firearms, parts, ammunition or explosives"],
  ["Drugs and regulated goods", "controlled substances, counterfeits, stolen or forged items"],
  ["Self-harm", "content promoting suicide, self-injury or disordered eating"],
  ["Harassment and doxxing", "targeted abuse, or someone's private details without consent"],
  ["Fraud and impersonation", "scams, phishing, and synthetic media passed off as real"],
  ["Malware and stolen data", "viruses, exploit code, or anything obtained through a breach"],
  ["Infringing material", "music, footage or images you do not have the rights to publish"],
];

export function ContentPolicyNotice({ where }: { where: "library" | "compose" }) {
  return (
    <details className="policy-notice">
      <summary>
        <strong>Before you upload:</strong> restricted content is not allowed
        {where === "compose" ? " here or on the networks you publish to" : " in your library"}.
        {" "}Breaking this rule can suspend your account for up to a month, or end it
        permanently — with no refund. <span className="policy-more">What counts?</span>
      </summary>
      <div className="policy-body">
        <ul>
          {CATEGORIES.map(([name, gloss]) => (
            <li key={name}><strong>{name}</strong> — {gloss}</li>
          ))}
        </ul>
        <p className="muted">
          This applies to files kept privately in your library too, not only to what you publish.
          Each network&rsquo;s own community standards apply on top. We do not review uploads in
          advance, but we do act on reports — see{" "}
          <Link to="/terms#restricted">Terms §11</Link>,{" "}
          <Link to="/terms#security">§12</Link> and{" "}
          <Link to="/terms#enforcement">§14</Link>, and report anything you find at{" "}
          <Link to="/terms#contact">the address here</Link>.
        </p>
      </div>
    </details>
  );
}
